import * as React from 'react';
import { useSelector } from 'react-redux';

import { trackHintQuestionBannerButtonClick, trackHintQuestionBannerShow } from './tracking';
import { HintQuestionBanner } from '../../components/HintQuestions/HintQuestionBanner';
import { useSendMessage } from '../../hooks/useSendMessage';
import { currentOpenChatSelector } from '../../selectors';
import { hintQuestionBannerSelector } from '../../selectors/hintQuestionsSelector';
import { EDeviceType } from '../../types/deviceType';
import { useApplicationContext } from '../../utils/applicationContext';
import { useRuntimeSetting } from '../../hooks/useRuntimeSetting';

export const HintQuestionBannerContainer: React.FC = () => {
  const {
    custom: { realDevice },
  } = useApplicationContext();
  const currentChat = useSelector(currentOpenChatSelector);
  const banner = useSelector(hintQuestionBannerSelector);
  const { onSend } = useSendMessage(true);

  const isBannerEnabled = useRuntimeSetting<boolean>('frontend-chats.subsidizedMortgage.Enabled', false);

  const onShow = React.useCallback(() => {
    /* istanbul ignore next */
    if (!currentChat || !banner?.primaryButton) {
      return;
    }

    const { offer } = currentChat;
    trackHintQuestionBannerShow(offer);
  }, [banner, currentChat]);

  const onPrimaryButtonClick = React.useCallback(() => {
    /* istanbul ignore next */
    if (!currentChat || !banner?.primaryButton) {
      return;
    }

    const { offer } = currentChat;
    trackHintQuestionBannerButtonClick(offer, banner.primaryButton.text);
    onSend({
      text: banner.primaryButton.messageText || '',
      chatId: currentChat.chatId,
      payload: banner.primaryButton.payload,
    });
  }, [currentChat, banner, onSend]);

  const onSecondaryButtonClick = React.useCallback(() => {
    /* istanbul ignore next */
    if (!currentChat || !banner?.secondaryButton) {
      return;
    }

    const { offer } = currentChat;
    trackHintQuestionBannerButtonClick(offer, banner.secondaryButton.text);

    window.open(banner.secondaryButton.deeplinkUrl ?? undefined, '_blank');
  }, [currentChat, banner]);

  if (!isBannerEnabled || !banner) {
    return null;
  }

  const { title, description, iconUrl, primaryButton, secondaryButton } = banner;

  return (
    <HintQuestionBanner
      title={title}
      description={description}
      primaryButtonText={primaryButton?.text || ''}
      secondaryButtonText={secondaryButton?.text || ''}
      iconUrl={iconUrl}
      isMobile={realDevice !== EDeviceType.Desktop}
      onPrimaryButtonClick={onPrimaryButtonClick}
      onSecondaryButtonClick={onSecondaryButtonClick}
      onShow={onShow}
    />
  );
};
